import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader.js";
import { useScroll } from "../../scroll/ScrollProvider";

/**
 * CryptoCoin — a heavyweight two-sided coin. Obverse: Bitcoin "₿".
 * Reverse: Ethereum diamond. Beveled body with milled (knurled) rim, both
 * symbols extruded from SVG paths so they read as embossed metal under the
 * cinematic lighting. Slow continuous spin; speeds up while you're inside
 * section 3 (Digital Assets).
 */

// Bitcoin "₿" glyph as an SVG path. Drawn to be roughly centered in a 100x100 box.
const BTC_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <path d="
    M44 14 L44 24
    L36 24 L36 76
    L44 76 L44 86
    L52 86 L52 76
    L58 76 L58 86
    L66 86 L66 75.6
    Q76 73.6 76 63
    Q76 55 70 51.4
    Q74 48 74 41
    Q74 30 64 26.6
    L64 14 L56 14 L56 25.4
    L52 25.4 L52 14 Z
    M46 32 L60 32 Q66 32 66 39 Q66 46 60 46 L46 46 Z
    M46 54 L62 54 Q68 54 68 61 Q68 68 62 68 L46 68 Z
  " fill="#000"/>
</svg>
`;

// Ethereum diamond — two stacked triangles forming the iconic ETH logo.
const ETH_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <path d="M50 10 L20 52 L50 66 L80 52 Z" fill="#000"/>
  <path d="M50 70 L20 56 L50 92 L80 56 Z" fill="#000"/>
</svg>
`;

function loadShape(svg: string): THREE.Shape[] {
  const loader = new SVGLoader();
  const data = loader.parse(svg);
  const shapes: THREE.Shape[] = [];
  for (const path of data.paths) {
    const pathShapes = SVGLoader.createShapes(path);
    for (const s of pathShapes) shapes.push(s);
  }
  return shapes;
}

function symbolMesh(svg: string, scale: number, depth: number): THREE.BufferGeometry {
  const shapes = loadShape(svg);
  const geom = new THREE.ExtrudeGeometry(shapes, {
    depth,
    bevelEnabled: true,
    bevelSegments: 3,
    bevelSize: 0.4,
    bevelThickness: 0.4,
    curveSegments: 24,
  });
  // SVG y is inverted vs. world y; flip and recenter
  geom.scale(scale, -scale, scale);
  geom.center();
  return geom;
}

const COIN_RADIUS = 1.5;
const COIN_THICKNESS = 0.32;

export function CryptoCoin() {
  const root = useRef<THREE.Group>(null);
  const halo = useRef<THREE.Points>(null);
  const { section, sectionProgress } = useScroll();

  // Build coin body: a beveled cylinder via LatheGeometry profile.
  const bodyGeom = useMemo(() => {
    const r = COIN_RADIUS;
    const h = COIN_THICKNESS / 2;
    const bevel = 0.06;
    const points: THREE.Vector2[] = [
      new THREE.Vector2(0, h),
      new THREE.Vector2(r - bevel, h),
      new THREE.Vector2(r, h - bevel),
      new THREE.Vector2(r, -h + bevel),
      new THREE.Vector2(r - bevel, -h),
      new THREE.Vector2(0, -h),
    ];
    return new THREE.LatheGeometry(points, 96);
  }, []);

  // Knurled (milled) edge — a thin ring of vertical micro-bars around the rim
  const knurlGeom = useMemo(() => {
    return new THREE.CylinderGeometry(
      COIN_RADIUS + 0.005,
      COIN_RADIUS + 0.005,
      COIN_THICKNESS - 0.04,
      192,
      1,
      true,
    );
  }, []);

  const btcGeom = useMemo(() => symbolMesh(BTC_SVG, 0.0185, 0.045), []);
  const ethGeom = useMemo(() => symbolMesh(ETH_SVG, 0.018, 0.045), []);

  // Halo of small gold sparkle points behind the coin
  const haloGeom = useMemo(() => {
    const positions: number[] = [];
    const count = 220;
    for (let i = 0; i < count; i++) {
      const r = 2.0 + Math.random() * 1.6;
      const a = Math.random() * Math.PI * 2;
      const y = (Math.random() - 0.5) * 0.6;
      positions.push(Math.cos(a) * r, y, Math.sin(a) * r);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return g;
  }, []);

  useFrame((_state, delta) => {
    const active = section < 2 ? 0 : section === 2 ? sectionProgress : 1;
    const baseSpin = 0.1;
    const accel = active * 0.18;
    if (root.current) {
      root.current.rotation.y += delta * (baseSpin + accel);
      // Gentle wobble — feels weighty, not motorized
      root.current.rotation.x = Math.sin(performance.now() * 0.00045) * 0.12;
    }
    if (halo.current) {
      halo.current.rotation.y -= delta * 0.05;
      const m = halo.current.material as THREE.PointsMaterial;
      m.opacity = 0.3 + active * 0.5;
    }
  });

  // Reusable gold material for body + symbols
  const goldMat = (
    <meshStandardMaterial
      color="#e7c98a"
      metalness={1}
      roughness={0.22}
      emissive="#3a2a0a"
      emissiveIntensity={0.25}
    />
  );

  return (
    <group ref={root}>
      {/* Body — laid flat (lathe is around Y, so rotate so the flat faces look at +Z/-Z) */}
      <mesh geometry={bodyGeom} rotation={[Math.PI / 2, 0, 0]} castShadow receiveShadow>
        {goldMat}
      </mesh>

      {/* Knurled rim ring (slightly darker for contrast) */}
      <mesh geometry={knurlGeom} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial
          color="#b9892f"
          metalness={1}
          roughness={0.55}
          emissive="#1a1006"
          emissiveIntensity={0.2}
        />
      </mesh>

      {/* BTC face — front (+Z after rotation) */}
      <mesh
        geometry={btcGeom}
        position={[0, 0, COIN_THICKNESS / 2 + 0.001]}
        castShadow
      >
        <meshStandardMaterial
          color="#f6d28a"
          metalness={1}
          roughness={0.18}
          emissive="#5a3c0a"
          emissiveIntensity={0.45}
          toneMapped={false}
        />
      </mesh>

      {/* ETH face — back. Flip 180° so it reads correctly when seen from -Z */}
      <mesh
        geometry={ethGeom}
        position={[0, 0, -COIN_THICKNESS / 2 - 0.001]}
        rotation={[0, Math.PI, 0]}
        castShadow
      >
        <meshStandardMaterial
          color="#f6d28a"
          metalness={1}
          roughness={0.18}
          emissive="#5a3c0a"
          emissiveIntensity={0.45}
          toneMapped={false}
        />
      </mesh>

      {/* Decorative inner ring on each face */}
      <mesh position={[0, 0, COIN_THICKNESS / 2 + 0.001]} rotation={[0, 0, 0]}>
        <torusGeometry args={[COIN_RADIUS * 0.82, 0.012, 12, 128]} />
        <meshStandardMaterial color="#b9892f" metalness={1} roughness={0.35} />
      </mesh>
      <mesh position={[0, 0, -COIN_THICKNESS / 2 - 0.001]}>
        <torusGeometry args={[COIN_RADIUS * 0.82, 0.012, 12, 128]} />
        <meshStandardMaterial color="#b9892f" metalness={1} roughness={0.35} />
      </mesh>

      {/* Halo of sparkle dust */}
      <points ref={halo} geometry={haloGeom}>
        <pointsMaterial
          size={0.04}
          color="#e7c98a"
          transparent
          opacity={0.5}
          depthWrite={false}
          sizeAttenuation
          toneMapped={false}
        />
      </points>

      {/* Localized warm fill so the gold pops */}
      <pointLight position={[0, 0.8, 1.6]} intensity={1.4} color="#f6d28a" distance={6} decay={2} />
    </group>
  );
}
